import { CourseDto } from 'api/generated';
import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'store';
import styled from 'styled-components';

import {
  BRAND_GREEN,
  BRAND_INK,
  ELITE_STACKS,
  ELITE_STACK_EXTRAS,
  ESSENTIAL_STACKS,
  MASTER_SLOT_ORDER,
  PLAN_START_DOLLARS,
  TIER_LABEL,
  TIER_ORDER,
  accentForSlug,
  cdnCoverForSlug,
  cdnIconForSlug,
  formatDollars,
  formatUsd,
  planMonthlyDollars,
  planSavingsDollars,
  tierBlurb,
} from '../bundleBuilderConfig';
import { BundleBuilderCatalog, BundleBuilderEntry, resolveBundleForPicks } from '../useBundleBuilderCatalog';
import { useUpsellAccent } from '../useUpsellAccent';
import { BundleBoxSet } from './BundleBoxSet';

/**
 * Height of the stacks panel row, in px.
 *
 * Fixed on purpose: sized once for the tallest package (a row of stack cards under two lines of
 * status text) so switching package never moves the box or the panel borders.
 */
const STACKS_ROW_HEIGHT = 176;

/** Chrome accent — follows the active course via the CSS var set on the popup root. */
const ACCENT = 'var(--accent, #ED5A26)';

/** "IPTA SCS" -> "SCS". The prefix repeats on every certification, so it only costs width. */
const shortCertName = (name?: string) => (name ?? '').replace(/^IPTA\s+/i, '') || '—';

type Props = {
  catalog: BundleBuilderCatalog | null;
  isLoading: boolean;
  error: string | null;
  sourceCourseId: string | null;
  onContinue: (bundle: CourseDto) => void;
  onClose: () => void;
};

/**
 * The in-LMS Bundle Builder.
 *
 * Modelled on the marketing builder at traineracademy.org/bundle-builder, deliberately stripped
 * back to what a signed-in learner needs. Tyler's brief was "super simplified — not as fancy or as
 * much marketing wow": the learner already knows what IPTA is, so the NCCA badge, the sales copy,
 * the trial card, the pricing table and the "your path" section are all dropped. What survives is
 * the part that does the job — pick a package, see what is in the box, swap certifications, check
 * out.
 *
 * Layout follows the annotated screenshot: packages on the left, the box in the middle, and the
 * certification picker moved into the right sidebar where the marketing column used to be.
 *
 * Everything is driven by the catalogue rather than a hardcoded config, so package sizes and prices
 * come from what is actually configured in admin.
 */
export const BundleBuilderContent = ({ catalog, isLoading, error, onContinue }: Props) => {
  const [selectedTier, setSelectedTier] = useState<BundleBuilderEntry['tier'] | null>(null);
  const [picks, setPicks] = useState<string[]>([]);
  // Set by the chooser: the bundle to open on ("Change your bundle") and the certifications the
  // learner already holds paid access to, so the picker can say so.
  const seed = useSelector(selectors.courses.selectBundleBuilderSeed);
  const { vars: accentVars } = useUpsellAccent();
  const ownedCertIds = useSelector(selectors.courses.selectBundleBuilderOwnedCertIds);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const accessToken = useSelector(selectors.auth.selectAccessToken);

  const getToken = useCallback(
    () =>
      accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      '',
    [accessToken],
  );

  // One representative bundle per tier describes the package; the exact combination the learner
  // ends up buying is resolved from their picks when they continue.
  const tiers = useMemo(() => {
    const byTier = new Map<BundleBuilderEntry['tier'], BundleBuilderEntry>();
    for (const bundle of catalog?.bundles ?? []) {
      if (!byTier.has(bundle.tier)) byTier.set(bundle.tier, bundle);
    }
    return TIER_ORDER.filter((t) => byTier.has(t)).map((t) => byTier.get(t) as BundleBuilderEntry);
  }, [catalog]);

  const certById = useMemo(() => new Map((catalog?.certs ?? []).map((cert) => [cert.courseId, cert])), [catalog]);

  const active = useMemo(() => tiers.find((t) => t.tier === selectedTier) ?? null, [tiers, selectedTier]);

  // Package size comes from the bundle's real membership, not from the tier name. A bundle tagged
  // Elite but built with two certifications then asks for the right number of picks instead of
  // demanding four and never resolving.
  const certCount = active?.memberCourseIds.length ?? 0;

  // Master is every certification by definition, so all of its members are fixed. Every other
  // package locks only the CPT.
  const isMaster = active?.tier === 'master';
  const lockedIds = useMemo(() => {
    if (!active) return [];
    if (isMaster) return active.memberCourseIds;
    return active.primaryCourseId ? [active.primaryCourseId] : [];
  }, [active, isMaster]);

  const slots = Math.max(0, certCount - lockedIds.length);

  /** Seeds a package with the exact set its representative bundle contains. */
  const seedPicks = useCallback((entry: BundleBuilderEntry) => {
    const primary = entry.tier === 'master' ? null : entry.primaryCourseId;
    return entry.memberCourseIds.filter((id) => id !== primary && entry.tier !== 'master');
  }, []);

  // Open on the learner's own bundle when the chooser handed one over ("Change your bundle"),
  // otherwise on the largest package with its contents filled in — never an empty picker.
  useEffect(() => {
    if (selectedTier || !tiers.length) return;

    if (seed && catalog) {
      // Match the seed to a package by tier name first, then by member count — a legacy bundle
      // has no tier but its 2 members still shape an Essential-sized selection.
      const target =
        tiers.find((t) => t.tier === seed.tier) ??
        tiers.find((t) => t.memberCourseIds.length === seed.memberSlugs.length);

      if (target) {
        const idBySlug = new Map(catalog.certs.map((cert) => [cert.slug, cert.courseId]));
        const primary = target.tier === 'master' ? null : target.primaryCourseId;
        const seededPicks =
          target.tier === 'master'
            ? []
            : seed.memberSlugs
                .map((slug) => idBySlug.get(slug))
                .filter((id): id is string => Boolean(id) && id !== primary);

        setSelectedTier(target.tier);
        // Only accept a seed that fills the package exactly; a partial map (renamed slug, missing
        // cert) falls back to the package's own default so the learner never starts under-filled.
        const slotsForTarget = target.memberCourseIds.length - (primary ? 1 : 0);
        setPicks(target.tier !== 'master' && seededPicks.length === slotsForTarget ? seededPicks : seedPicks(target));
        return;
      }
    }

    const first = tiers[0];
    setSelectedTier(first.tier);
    setPicks(seedPicks(first));
  }, [tiers, selectedTier, seedPicks, seed, catalog]);

  const chooseTier = (entry: BundleBuilderEntry) => {
    if (entry.tier === selectedTier) return;
    setSelectedTier(entry.tier);
    setPicks(seedPicks(entry));
    setResolveError(null);
  };

  const togglePick = (courseId: string) => {
    if (lockedIds.includes(courseId)) return;
    setResolveError(null);
    setPicks((current) => {
      if (current.includes(courseId)) return current.filter((id) => id !== courseId);
      if (current.length >= slots) return current;
      return [...current, courseId];
    });
  };

  const selectedIds = useMemo(() => [...lockedIds, ...picks], [lockedIds, picks]);
  const isComplete = Boolean(active) && picks.length === slots;

  /**
   * The slugs in the box, in slot order — this is what the animation positions books by.
   *
   * The full box uses the hero-render order so every texture sits in the slot it was photographed
   * in. Smaller packages run CPT first then the learner's picks in the order they chose them, so a
   * newly added book slides into the next free slot instead of reshuffling the whole row.
   */
  const boxSlugs = useMemo(() => {
    const slugs = selectedIds.map((id) => certById.get(id)?.slug).filter((slug): slug is string => Boolean(slug));
    return certCount >= 6 ? MASTER_SLOT_ORDER.filter((slug) => slugs.includes(slug)) : slugs;
  }, [selectedIds, certById, certCount]);

  // Savings are computed from what the individual certifications in this package actually cost,
  // so the figure moves with admin pricing instead of being copy that goes stale.
  // The landing page's "save $350": the plan total vs paying cash once. Cert-singles arithmetic
  // was tried first but placeholder certs have no prices, so it usually rendered nothing.
  const savings = useMemo(
    () => (active ? planSavingsDollars(active.prices.purchaseAmount, active.prices.installmentAmount) : undefined),
    [active],
  );

  const certBySlug = useMemo(() => new Map((catalog?.certs ?? []).map((cert) => [cert.slug, cert])), [catalog]);

  /**
   * Quick-pick stacks — the named combinations from the marketing builder.
   *
   * They are shortcuts, not extra products: each one resolves to a combination the learner could
   * assemble by hand from the covers below. Only offered when every certification the stack needs
   * is actually in the catalogue, so a stack can never seed an unresolvable selection.
   */
  const stacks = useMemo(() => {
    if (!active || isMaster) return [];
    const presets = active.tier === 'elite' ? ELITE_STACKS : ESSENTIAL_STACKS;
    const base = active.tier === 'elite' ? ELITE_STACK_EXTRAS : [];
    const primarySlug = active.primaryCourseId ? certById.get(active.primaryCourseId)?.slug : undefined;

    return presets
      .map((preset) => {
        const picks = [...base, preset.specialty];
        return {
          ...preset,
          picks,
          // Everything the stack ends up containing, locked CPT included — this is what the card
          // shows, so the learner can tell the stacks apart without clicking each one.
          memberSlugs: [primarySlug, ...picks].filter((slug): slug is string => Boolean(slug)),
        };
      })
      .filter((preset) => preset.picks.every((slug) => certBySlug.has(slug)) && preset.picks.length === slots);
  }, [active, isMaster, certBySlug, certById, slots]);

  const applyStack = (stack: { picks: string[] }) => {
    setResolveError(null);
    setPicks(stack.picks.map((slug) => certBySlug.get(slug)?.courseId).filter((id): id is string => Boolean(id)));
  };

  /** The stack whose exact set the learner is currently on, so its chip can light up. */
  const activeStackId = useMemo(() => {
    const current = [...picks].sort().join('|');
    return (
      stacks.find(
        (stack) =>
          stack.picks
            .map((slug) => certBySlug.get(slug)?.courseId)
            .filter(Boolean)
            .sort()
            .join('|') === current,
      )?.id ?? null
    );
  }, [stacks, picks, certBySlug]);

  /**
   * Resolve the exact pick set to the ONE bundle containing precisely those certifications, then
   * hand that id to checkout.
   *
   * This must not shortcut to "the first bundle of the selected tier": with ten Elite rows that
   * would sell the buyer a different set than the one they chose. The backend enforces exact set
   * equality and 404s when the combination is not sold.
   */
  const handleContinue = async () => {
    if (!active || !isComplete) return;

    setIsResolving(true);
    setResolveError(null);

    try {
      const bundleId = await resolveBundleForPicks(selectedIds, getToken());

      if (!bundleId) {
        setResolveError('That combination is not sold as a package. Try a different selection.');
        return;
      }

      onContinue({ id: bundleId } as unknown as CourseDto);
    } catch (e) {
      setResolveError(e instanceof Error ? e.message : 'Could not continue. Please try again.');
    } finally {
      setIsResolving(false);
    }
  };

  if (isLoading) return <Message>Loading packages…</Message>;
  if (error) return <Message role="alert">{error}</Message>;
  if (!tiers.length) return <Message>No packages are available right now.</Message>;

  const remaining = slots - picks.length;
  const tierName = active ? TIER_LABEL[active.tier] : '';
  const activeMonthly = planMonthlyDollars(active?.prices.installmentAmount, active?.prices.commitmentPaymentAmount);

  /** The prompt in the right column, telling the learner what the box still needs. Marketing copy. */
  const statusLine = isMaster
    ? 'The Complete Stack — all six certifications. Zero decisions.'
    : remaining >= 3
    ? 'Choose three more certifications.'
    : remaining === 2
    ? 'Choose two more certifications.'
    : remaining === 1
    ? active?.tier === 'elite'
      ? 'Choose your last specialty to round out your Elite Trainer package.'
      : 'Choose your free second certification.'
    : `Your ${tierName} box is complete — swap any specialty below.`;

  return (
    <Root style={accentVars as React.CSSProperties}>
      <Scroll>
        <Columns>
          <TiersColumn>
            <StepHeading>
              Step 1: <em>Pick your package</em> <Arrow aria-hidden>↓</Arrow>
            </StepHeading>

            <Tiers>
              {tiers.map((entry) => {
                const count = entry.memberCourseIds.length;
                const selected = entry.tier === selectedTier;
                const perCert = entry.prices.purchaseAmount
                  ? Math.round(entry.prices.purchaseAmount / Math.max(1, count))
                  : undefined;
                // Same formula and wording as the marketing builder, so both surfaces quote
                // identical figures: $159/$139/$82 monthly, save $350/$330/$159.
                const monthly = planMonthlyDollars(
                  entry.prices.installmentAmount,
                  entry.prices.commitmentPaymentAmount,
                );
                const saved = planSavingsDollars(entry.prices.purchaseAmount, entry.prices.installmentAmount);

                return (
                  <TierCard key={entry.tier} type="button" $selected={selected} onClick={() => chooseTier(entry)}>
                    <TierTop>
                      <TierName>{TIER_LABEL[entry.tier]}</TierName>
                      <TierIcons>
                        {entry.memberSlugs.map((slug) => (
                          <TierIcon key={slug} src={cdnIconForSlug(slug)} alt="" loading="lazy" />
                        ))}
                      </TierIcons>
                    </TierTop>

                    <TierBlurb $selected={selected}>{tierBlurb(entry.tier, count)}</TierBlurb>

                    {monthly !== undefined && (
                      <TierPrice>
                        <strong>Start for {formatDollars(PLAN_START_DOLLARS)}</strong>
                        <span>then {formatDollars(monthly)}/mo</span>
                      </TierPrice>
                    )}

                    {entry.prices.purchaseAmount !== undefined && (
                      <TierOneTime>
                        or {formatUsd(entry.prices.purchaseAmount)}
                        {saved !== undefined && <em> — save {formatDollars(saved)}</em>}
                        {perCert !== undefined && <PerCertInline>{formatUsd(perCert)}/cert</PerCertInline>}
                      </TierOneTime>
                    )}

                    <TierBadge $accent={entry.tier === 'elite'}>{tierBlurb(entry.tier, count)}</TierBadge>
                  </TierCard>
                );
              })}
            </Tiers>
          </TiersColumn>

          <BoxColumn>
            <BundleBoxSet
              slugs={boxSlugs}
              certCount={certCount}
              complete={isComplete}
              title={active ? TIER_LABEL[active.tier] : ''}
            />
          </BoxColumn>

          {/* Step 2 — the picker, two per row. */}
          <PickerColumn>
            <StepHeading>
              Step 2: <em>{isMaster ? 'Everything included' : 'Pick your certifications'}</em>
            </StepHeading>

            <Covers>
              {(catalog?.certs ?? []).map((cert) => {
                const locked = lockedIds.includes(cert.courseId);
                const included = locked || picks.includes(cert.courseId);
                const full = !included && remaining <= 0;
                const accent = accentForSlug(cert.slug);
                const cover = cert.coverImageUrl || cdnCoverForSlug(cert.slug);
                const owned = ownedCertIds.includes(cert.courseId);

                return (
                  <CoverItem key={cert.courseId}>
                    <CoverButton
                      type="button"
                      disabled={locked || full}
                      aria-pressed={included}
                      aria-label={`${included ? 'Remove' : 'Add'} ${cert.name}`}
                      $included={included}
                      $accent={accent}
                      onClick={() => togglePick(cert.courseId)}>
                      {cover && <CoverImage src={cover} alt="" loading="lazy" $dim={!included} />}
                      {included && <IncludedTag $accent={accent}>Included</IncludedTag>}
                    </CoverButton>

                    <CoverCaption>
                      <CoverIcon src={cdnIconForSlug(cert.slug)} alt="" loading="lazy" />
                      <CoverName $muted={!included}>{cert.name}</CoverName>
                      {owned && <OwnedTag>You own this</OwnedTag>}
                    </CoverCaption>
                  </CoverItem>
                );
              })}
            </Covers>
          </PickerColumn>

          {/* Spans the packages and the box, sitting under both. */}
          <StacksSection>
            <StatusLine $complete={isComplete}>{statusLine}</StatusLine>

            {/*
             * Master has no choices to offer, so without this the panel is one line of text in a
             * wide empty box while every other package shows rich cards. It earns the space by
             * saying what "everything" actually means and listing it.
             */}
            {isMaster && (
              <>
                <StatusSub>Every IPTA certification, every textbook, the full MVP study system for each one.</StatusSub>
                <MasterCerts>
                  {boxSlugs.map((slug) => (
                    <StackCert key={slug} $active={false}>
                      <StackCertIcon src={cdnIconForSlug(slug)} alt="" loading="lazy" />
                      {shortCertName(certBySlug.get(slug)?.name)}
                    </StackCert>
                  ))}
                </MasterCerts>
              </>
            )}

            {stacks.length > 0 && (
              <>
                <StacksLabel>
                  {active?.tier === 'essential'
                    ? 'Five options — CPT + Nutrition is the most picked'
                    : `Most popular ${tierName} stacks`}
                </StacksLabel>
                <Stacks>
                  {stacks.map((stack) => {
                    const isActive = stack.id === activeStackId;

                    return (
                      <StackCard
                        key={stack.id}
                        type="button"
                        title={stack.description}
                        $active={isActive}
                        onClick={() => applyStack(stack)}>
                        <StackName $active={isActive}>{stack.name}</StackName>

                        <StackCerts>
                          {stack.memberSlugs.map((slug) => (
                            <StackCert key={slug} $active={isActive}>
                              <StackCertIcon src={cdnIconForSlug(slug)} alt="" loading="lazy" />
                              {shortCertName(certBySlug.get(slug)?.name)}
                            </StackCert>
                          ))}
                        </StackCerts>
                      </StackCard>
                    );
                  })}
                </Stacks>
              </>
            )}
          </StacksSection>
        </Columns>
      </Scroll>

      <Footer>
        <Summary>
          <SummaryTitle>
            {tierName || 'Select a package'}
            {active && ` · ${selectedIds.length} of ${certCount} certifications`}
          </SummaryTitle>
          {resolveError ? (
            <ErrorText role="alert">{resolveError}</ErrorText>
          ) : (
            active && (
              <SummaryPrice>
                {formatUsd(active.prices.purchaseAmount)}
                {activeMonthly !== undefined &&
                  ` · Start for ${formatDollars(PLAN_START_DOLLARS)}, then ${formatDollars(activeMonthly)}/mo`}
                {savings !== undefined && <Saving> — save {formatDollars(savings)}</Saving>}
              </SummaryPrice>
            )
          )}
        </Summary>

        <Continue type="button" disabled={!isComplete || isResolving} onClick={handleContinue}>
          {isResolving ? 'Checking…' : isComplete ? 'Continue' : `Pick ${remaining} more`}
        </Continue>
      </Footer>
    </Root>
  );
};

/*
 * Layout note: every vertical dimension below is capped so the whole builder fits one viewport
 * without scrolling. The box is sized in vh rather than px for that reason — it is the one element
 * that can give back space on a short screen, and a square that shrinks reads better than a column
 * that scrolls.
 */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 1260px;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
`;

/**
 * The scrolling middle of the shell.
 *
 * Everything above the action bar lives here, so on a short screen the content scrolls and the bar
 * stays put instead of being pushed off the bottom of the viewport.
 */
const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /*
   * Deliberately not scrollable at the three-column size. Every child below is either fixed-height
   * or shrink-to-fit, and the box soaks up the remainder, so there is nothing left to scroll.
   * Hiding it makes that a guarantee rather than a hope — a stray overflow shows as a clipped edge
   * in review instead of silently reintroducing the scrollbar this layout exists to remove.
   */
  overflow: hidden;

  /*
   * The stacked layout is the opposite case: tiers, box, picker and stacks sit one under another
   * and simply do not fit a phone, so the same rule clips the bottom of the builder instead of
   * guaranteeing anything. Scrolling here is what lets the covers be their true size rather than
   * whatever is left over.
   */
  @media (max-width: 1080px) {
    overflow-y: auto;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 270px minmax(0, 1fr) 310px;
  /*
   * Row 2 is a FIXED height, not auto.
   *
   * Auto sized it to whatever the selected package happened to need — one row of chips for Master,
   * three cards for Elite, five for Essential — so every click resized that row, which resized row
   * 1, which resized the box. Nothing should move when the learner is only comparing packages. A
   * constant row means the box is constant too, and the panel is built to fit the tallest case.
   */
  grid-template-rows: minmax(0, 1fr) ${STACKS_ROW_HEIGHT}px;
  grid-template-areas:
    'tiers box picker'
    'stacks stacks picker';
  gap: 14px 18px;
  align-items: start;
  flex: 1;
  min-height: 0;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    grid-template-areas:
      'tiers'
      'box'
      'picker'
      'stacks';
    /*
     * Sized by its content rather than by the viewport. Stretched to the builder's height, the four
     * stacked sections had to share a phone screen between them, so each one was squeezed below
     * what it needs and they overlapped; Scroll takes the overflow instead.
     */
    flex: 0 0 auto;
    min-height: auto;
  }
`;

const TiersColumn = styled.div`
  grid-area: tiers;
  /* Stretches to the row height — which the box sets — so the two columns end level. */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 0;
`;

const StepHeading = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${BRAND_INK};

  em {
    font-style: normal;
    color: var(--accent, #ed5a26);
  }
`;

const Arrow = styled.span`
  color: var(--accent, #ed5a26);
  font-weight: 900;
`;

const Tiers = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  /* Equal rows, so three cards fill the column beside the box instead of leaving a gap under it. */
  grid-auto-rows: 1fr;
  gap: 16px;
`;

const TierCard = styled.button<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  padding: 10px 14px;
  border-radius: 14px;
  cursor: pointer;
  background: #fff;
  transition: all 0.15s ease;
  border: 2px solid ${({ $selected }) => ($selected ? ACCENT : '#e5e7eb')};
  box-shadow: ${({ $selected }) =>
    $selected ? '0 10px 24px var(--accent-glow, var(--accent-mid, rgba(237, 90, 38, 0.28)))' : 'none'};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? ACCENT : '#d1d5db')};
  }
`;

const TierTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const TierName = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${BRAND_INK};
`;

const TierIcons = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const TierIcon = styled.img`
  height: 13px;
  width: auto;
`;

const TierBlurb = styled.div<{ $selected: boolean }>`
  margin-top: 1px;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $selected }) => ($selected ? ACCENT : '#6b7280')};
`;

const TierPrice = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: baseline;
  gap: 7px;

  strong {
    font-size: 18px;
    font-weight: 800;
    color: ${BRAND_INK};
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: ${BRAND_GREEN};
  }
`;

const TierOneTime = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: ${BRAND_GREEN};

  em {
    font-style: normal;
  }
`;

const PerCertInline = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
`;

const TierBadge = styled.span<{ $accent: boolean }>`
  position: absolute;
  bottom: -9px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
  background: ${({ $accent }) => ($accent ? ACCENT : BRAND_INK)};
`;

const BoxColumn = styled.div`
  grid-area: box;
  /* Takes the row's full height and hands it to the box, which measures itself into it. */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 0 auto;
  width: 100%;
  min-height: 0;
`;

const StatusLine = styled.p<{ $complete: boolean }>`
  flex-shrink: 0;
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.35;
  transition: color 0.3s ease;
  color: ${({ $complete }) => ($complete ? BRAND_INK : ACCENT)};
`;

const StacksSection = styled.div`
  grid-area: stacks;
  /* Fills the fixed row, so its own borders sit in the same place for every package. */
  align-self: stretch;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 12px 18px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
`;

const StatusSub = styled.p`
  margin: 6px 0 0;
  text-align: center;
  font-size: 13px;
  line-height: 1.45;
  color: #6b7280;
`;

/** The six certifications, centred — the visual answer to "what does everything include?". */
const MasterCerts = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
`;

const StacksLabel = styled.div`
  margin: 8px 0 8px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
`;

/**
 * Side by side across the strip.
 *
 * The strip spans the packages and the box, so there is real width here — enough for each stack to
 * show its certifications rather than hiding them behind a name. auto-fit lets Essential's five
 * wrap onto a second row without a separate rule.
 */
const Stacks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
`;

const StackCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  border: 2px solid ${({ $active }) => ($active ? ACCENT : '#e5e7eb')};
  background: ${({ $active }) => ($active ? 'var(--accent-soft, rgba(237, 90, 38, 0.06))' : '#fff')};

  &:hover {
    border-color: var(--accent, #ed5a26);
  }
`;

const StackName = styled.span<{ $active: boolean }>`
  font-size: 13px;
  font-weight: 800;
  color: ${({ $active }) => ($active ? ACCENT : BRAND_INK)};
`;

const StackCerts = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

/** One chip per certification in the stack — the whole point is that the contents are visible. */
const StackCert = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px 2px 4px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? BRAND_INK : '#4b5563')};
  background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.9)' : '#f3f4f6')};
`;

const StackCertIcon = styled.img`
  height: 10px;
  width: auto;
`;

const PickerColumn = styled.div`
  grid-area: picker;
  /* Spans both rows, so the covers divide the full height of the builder. */
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  min-height: 0;

  /* Stacked it is one section among four, sized by the covers rather than by the builder. */
  @media (max-width: 1080px) {
    height: auto;
  }
`;

/**
 * Two per row.
 *
 * The covers are 363x470, so a two-wide grid is inherently tall — it only fits because moving the
 * picker here reclaims the entire bottom strip it used to occupy. `auto-rows: 1fr` keeps the three
 * rows equal so the grid divides the column height evenly however much of it there is.
 */
const Covers = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: 10px;
  overflow-y: auto;

  /*
   * Stacked, the grid has no height of its own to divide, so equal 1fr rows become equal shares of
   * whatever the other three sections left over — which was less than a cover is tall, and the
   * cards spilled over each other. Rows follow the cards instead and Scroll takes the overflow.
   */
  @media (max-width: 1080px) {
    flex: 0 0 auto;
    grid-auto-rows: auto;
    overflow-y: visible;
  }
`;

const CoverItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
  min-height: 0;
`;

/**
 * The cover art's own ratio — the CDN files are all 363x470, and an admin-set `coverImageUrl` is
 * expected to match. Stated on the image so its box is the right shape in every layout, rather
 * than inferred from whichever axis the layout happens to constrain.
 */
const COVER_RATIO = '363 / 470';

const CoverButton = styled.button<{ $included: boolean; $accent: string }>`
  position: relative;
  /*
   * Wraps the art rather than sizing it: the frame is the art's box plus this border, so the
   * coloured edge hugs the book on all four sides. Giving the FRAME the ratio instead leaves the
   * art letterboxed inside it — aspect-ratio describes the border box, so the content box in
   * there is 6px short on both axes and no longer the shape of a cover.
   */
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  border-radius: 10px;
  background: transparent;
  line-height: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: all 0.15s ease;
  border: 3px solid ${({ $included, $accent }) => ($included ? $accent : '#e5e7eb')};
  box-shadow: ${({ $included, $accent }) => ($included ? `0 0 14px ${$accent}55` : 'none')};
  opacity: ${({ disabled, $included }) => (disabled && !$included ? 0.4 : 1)};

  /*
   * Deliberately no transform on hover. Scaling the cover reads as the row changing height even
   * though layout is untouched, which is distracting in a six-up grid. Colour and shadow carry the
   * affordance instead.
   */
  &:hover {
    border-color: ${({ disabled, $included, $accent }) => (disabled ? undefined : $included ? $accent : '#c9ccd1')};
    box-shadow: ${({ disabled, $accent }) => (disabled ? undefined : `0 0 14px ${$accent}44`)};
  }

  /*
   * Stacked, the row sizes to its content, so there is no row height to take. The cell's width is
   * the one definite axis in that layout — the frame takes it and the art below derives its height
   * from it.
   */
  @media (max-width: 1080px) {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
  }
`;

const CoverImage = styled.img<{ $dim: boolean }>`
  display: block;
  /*
   * The art carries the ratio — stated rather than left to the file's intrinsic size, so the box is
   * the right shape before the image decodes and whatever the layout hands it, only ONE axis has to
   * be definite.
   *
   * Beside the box both are constrained and height binds first, the covers being portrait; stacked,
   * the frame above is the cell's width and the height follows from the ratio. That second case is
   * what went wrong: with both axes auto, max-height:100% had nothing to resolve against, so the
   * art fell back towards its natural 363px and the frame ended up whatever shape was left over.
   *
   * object-fit stays the guarantee rather than the layout: a cover that ISN'T 363x470 — an
   * admin-set coverImageUrl — letterboxes inside the frame instead of stretching or cropping.
   */
  aspect-ratio: ${COVER_RATIO};
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: filter 0.2s ease;
  filter: ${({ $dim }) => ($dim ? 'saturate(0.6) opacity(0.72)' : 'none')};

  @media (max-width: 1080px) {
    width: 100%;
    height: auto;
  }
`;

const IncludedTag = styled.span<{ $accent: string }>`
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 4px;
  color: #fff;
  background: ${({ $accent }) => $accent};
`;

const CoverCaption = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
`;

const CoverIcon = styled.img`
  height: 11px;
  width: auto;
  flex-shrink: 0;
`;

const CoverName = styled.span<{ $muted: boolean }>`
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $muted }) => ($muted ? '#9ca3af' : BRAND_INK)};
`;

/** Marks certifications the learner already holds paid access to. Informational, not a blocker —
 * packages are sold whole, so an owned cert can still sit inside one (edge case #4, open). */
const OwnedTag = styled.span`
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 4px;
  color: #fff;
  background: ${BRAND_GREEN};
  flex-shrink: 0;
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  /* Full-bleed across the modal's 22px padding so it reads as a bar, not a boxed row. */
  margin: 0 -22px -22px;
  padding: 14px 28px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e5e7eb;
  background: ${({ theme: { colors } }) => colors.neutrals[11]};
`;

const Summary = styled.div`
  min-width: 0;
`;

const SummaryTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${BRAND_INK};
`;

const SummaryPrice = styled.div`
  font-size: 12px;
  color: #4b5563;
`;

const Saving = styled.span`
  color: ${BRAND_GREEN};
  font-weight: 700;
`;

const Continue = styled.button`
  flex-shrink: 0;
  padding: 12px 30px;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
  background: var(--accent, #ed5a26);
  cursor: pointer;

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #c0392b;
`;

const Message = styled.p`
  /* Loading / error / empty each early-return in place of the ENTIRE builder. The modal container
     is a 100vh flex column, so growing into the free space and centring both axes puts the text in
     the middle of the screen rather than the top-left corner of an otherwise blank page. */
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  opacity: 0.8;
`;
